'use client';

import { cn } from '~/lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, MinusIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

type QuantityInputProps = {
  size?: 'basket' | 'plp' | 'quickview' | 'pdp';
  className?: string;
  quantity: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  showDeleteIcon?: boolean;
  minQuantity?: number;
  isLoading?: boolean;
};

export function QuantityInput({
  className,
  disabled,
  size = 'basket',
  showDeleteIcon,
  quantity,
  minQuantity = 1,
  onChange,
  isLoading,
}: QuantityInputProps) {
  const [inputValue, setInputValue] = useState(quantity.toString());

  useEffect(() => {
    setInputValue(quantity.toString());
  }, [quantity]);

  const minusIcon =
    showDeleteIcon && quantity <= minQuantity ? (
      <>
        <span className="sr-only">Remove item from basket</span>
        <TrashIcon className="size-4 text-current" />
      </>
    ) : (
      <>
        <span className="sr-only">Decrease quantity</span>
        <MinusIcon className="size-4" />
      </>
    );

  function checkMinusIsTabbable() {
    if (showDeleteIcon && quantity <= minQuantity) {
      return true;
    }

    if (quantity > minQuantity) {
      return true;
    }

    return false;
  }

  const minusIsTabbable = checkMinusIsTabbable() === true ? undefined : -1;

  return (
    <div
      className={cn(
        'quantity-input',
        {
          'quantity-input--basket': size === 'basket',
          'quantity-input--plp': size === 'plp',
          'quantity-input--pdp': size === 'pdp',
          'quantity-input--quickview': size === 'quickview',
        },
        'relative flex items-center justify-center',
        {
          'w-full': size === 'basket' || size === 'plp',
        },
        className,
      )}
      data-testid="quantity-input"
    >
      <Button
        type="button"
        variant="outline"
        size="icon"
        tabIndex={minusIsTabbable}
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        disabled={disabled || isLoading}
        data-testid="decrease-quantity"
        className={cn(
          'quantity-input__button',
          'z-10 basis-1/3 !rounded-r-none rounded-l-pill focus-visible:z-30',
          {
            'h-8': size === 'basket',
            'h-10 min-w-12': size === 'plp',
            'h-10 min-w-10': size === 'quickview',
          },
          quantity === 0 && showDeleteIcon && '!text-transparent', // Hide the icon when quantity is 0 as this should trigger the delete
        )}
        onClick={() => {
          const newQuantity = quantity - 1;

          if (newQuantity < minQuantity) {
            if (showDeleteIcon) {
              onChange(0);
              return;
            } else if (minQuantity > 1) {
              //MOQ cant remove by decrement toast message
              toast.error(
                `The minimum order quantity for this product is ${minQuantity}, please delete the item if no longer required`,
                { id: 'moq-warning-toast' },
              );
            }
          }

          const finalQuantity = Math.max(minQuantity, newQuantity);
          onChange(finalQuantity);
        }}
        loader={quantity === 0 && showDeleteIcon}
      >
        {minusIcon}
      </Button>
      <div
        className={cn('relative z-20 basis-1/3 focus-within:z-30', {
          'h-8 max-w-10': size === 'basket',
          'h-10 grow': size === 'plp',
          'h-10 w-12': size === 'quickview',
          'h-10': size === 'pdp',
        })}
      >
        <Input
          data-hj-allow
          data-testid="quantity-input-value"
          onKeyDown={(e) => {
            if (
              e.key === 'e' ||
              e.key === '-' ||
              e.key === '+' ||
              e.key === '.' ||
              e.key === 'E'
            ) {
              e.preventDefault();
            }
          }}
          type="number"
          inputMode="numeric"
          value={inputValue ?? undefined}
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          disabled={disabled || isLoading}
          onChange={(e) => {
            setInputValue(e.target.value);
            const parsedValue = parseInt(e.target.value);
            if (
              typeof parsedValue === 'number' &&
              !isNaN(parsedValue) &&
              (parsedValue >= minQuantity || parsedValue === 0)
            ) {
              onChange(parsedValue);
            }
          }}
          className={cn(
            'quantity-input__input',
            'h-full w-full rounded-none border-x-0 border-primary px-1 text-center transition-colors',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            { 'text-transparent': isLoading },
          )}
          min={minQuantity}
          max={9999}
          step={1}
          pattern="[0-9]*"
          onBlur={() => setInputValue(quantity.toString())}
        />
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <Loader2 className="size-[1.2em] animate-spin opacity-40" />
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        disabled={disabled || isLoading}
        className={cn(
          'quantity-input__button z-10 basis-1/3 !rounded-l-none rounded-r-pill focus-visible:z-30',
          {
            'h-8': size === 'basket',
            'h-10 min-w-12': size === 'plp',
            'h-10 min-w-10': size === 'quickview',
          },
        )}
        onClick={() => onChange(quantity + 1)}
        data-testid="increase-quantity"
      >
        <span className="sr-only">Increase quantity</span>
        <PlusIcon className="size-4" />
      </Button>
    </div>
  );
}
